import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { shadows } from '@/components/business/design-system/businessDesignTokens';
import { optimizedImage } from '@/lib/cloudinaryImage';
import { cn } from '@/lib/utils';
import { ActivityForHomepage } from '@/types/homepage';

interface PartnerTourCardProps {
  activity: ActivityForHomepage;
  fallbackImage?: string;
  href?: string;
  onLinkClick: () => void;
}

// Read-only tour card: visitors book on the operator's own site, so it never links to the booking flow.
export function PartnerTourCard({ activity, fallbackImage, href, onLinkClick }: PartnerTourCardProps) {
  const { t } = useTranslation('public', { keyPrefix: 'business.profile' });
  const seasons = (activity.seasons ?? []).filter((season) => season !== 'all');
  const price = activity.price?.amount
    ? new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: activity.price.currency,
        maximumFractionDigits: 0,
      }).format(activity.price.amount)
    : null;

  return (
    <Card className={cn(shadows.level1.className, 'flex flex-col overflow-hidden border-slate-200/60')}>
      <div className="aspect-[16/9] w-full overflow-hidden bg-gray-200">
        <img
          src={optimizedImage(activity.images?.[0] || fallbackImage, 900) || '/images/default-activity.jpg'}
          alt={activity.name}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        {seasons.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {seasons.map((season) => (
              <Badge key={season} variant="outline" className="border-primary/30 text-primary">
                {t(`seasons.${season}`)}
              </Badge>
            ))}
          </div>
        )}
        <h3 className="text-lg font-semibold text-slate-900">{activity.name}</h3>
        <p className="flex-1 text-sm text-slate-600">{activity.description}</p>
        {price && <p className="text-sm font-medium text-slate-900">{t('tours.from', { price })}</p>}
        {href && (
          <Button asChild variant="outline" className="w-full border-primary text-primary hover:bg-primary/5 hover:text-primary">
            <a href={href} target="_blank" rel="noopener noreferrer" onClick={onLinkClick}>
              {t('tours.viewOnSite')}
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
